/**
 * Central icon registry. Wraps lucide-react components into named buckets
 * so callers don't have to import individual icons and pick stroke
 * widths per-call.
 *
 * Visual conventions:
 *   - Stroke width 1.75 — slightly heavier than Lucide default for
 *     premium feel without going chunky
 *   - Sizes: 16 (nav rail), 18 (inline w/ text), 20 (mode cards), 24 (hero)
 *   - currentColor for fill/stroke so badges + buttons style via CSS
 */

import {
  ArrowDownRight,
  ArrowUpRight,
  Award,
  BarChart3,
  BookOpen,
  Brain,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Crosshair,
  Dumbbell,
  Flag,
  Flame,
  LineChart,
  Library,
  Lock,
  Medal,
  MessageCircle,
  MessageSquare,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Settings as SettingsIcon,
  Shield,
  Shuffle,
  Sigma,
  Snowflake,
  Sparkles,
  Star,
  Target,
  Trash2,
  TrendingDown,
  TrendingUp,
  Trophy,
  Users,
  Wallet,
  X,
  Zap,
} from "lucide-react";

import type { SessionMode } from "@/shared/models";

/** Standard stroke for every icon — set once so the visual language stays consistent. */
export const ICON_STROKE = 1.75;

/** Top-level tab nav icons. */
export const NavIcons = {
  drills: Target,
  skills: TrendingUp,
  tracker: LineChart,
  analyzer: Search,
  study: BookOpen,
  quizzes: Brain,
  recent: Clock,
  feedback: MessageSquare,
  settings: SettingsIcon,
  admin: Shield,
} as const;

/** Drill-mode icons. */
export const MODE_ICONS: Record<SessionMode, typeof Target> = {
  spot_drill: Crosshair,
  concept_deep_dive: BookOpen,
  archetype_drill: Users,
  switching_framework: RefreshCw,
  just_ask: MessageCircle,
  session_warmup: Flame,
  session_debrief: Snowflake,
  session_consult: BarChart3,
  hand_analysis: Search,
};

// Re-export commonly-used Lucide components so consumers can grab them
// without duplicating the import path. Caller still passes their own
// size / className.
export {
  Sparkles,
  Shuffle,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  ArrowUpRight,
  ArrowDownRight,
  Brain,
  Flag,
  Flame,
  Lock,
  Dumbbell,
  Sigma,
  Target,
  Crosshair,
  TrendingUp,
  BookOpen,
  Clock,
  Users,
  RefreshCw,
  Library,
  Search,
  Award,
  Medal,
  Star,
  Trophy,
  Zap,
  LineChart,
  BarChart3,
  Snowflake,
  Wallet,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  TrendingDown,
};
